/**
 * API Service Layer
 * Handles all API communication with clean abstractions
 */

import { API_CONFIG } from '../config/api';
import type {
  PanchangaResponse,
  WeeklyPanchangaResponse,
  MonthlyPanchangaResponse,
  FestivalResponse,
  LocationInfo,
  HealthResponse,
  SupportedLocales,
  PanchangaRequestParams,
  WeeklyPanchangaRequestParams,
  MonthlyPanchangaRequestParams,
  FestivalRequestParams,
  TaraBalamResponse,
  TaraBalamRequestParams,
  MuhurthaResponse,
  MuhurthaRequestParams,
  BirthChartResponse,
  BirthChartRequestParams,
  CitySearchResult,
  DailyBestTimesResponse,
  DailyBestTimesRequestParams,
} from '../types/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public detail?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class PanchangaApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_CONFIG.baseUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetchApi<T>(
    endpoint: string,
    params?: PanchangaRequestParams | WeeklyPanchangaRequestParams | MonthlyPanchangaRequestParams | FestivalRequestParams | Record<string, string | number | undefined>
  ): Promise<T> {
    const base = this.baseUrl || window.location.origin;
    const url = new URL(`${base}${endpoint}`);

    if (params) {
      Object.entries(params as Record<string, string | number | undefined>).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    try {
      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.detail || `API error: ${response.status}`,
          response.status,
          errorData.detail
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      // Handle network errors (API server not running, CORS, etc.)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(
          'Cannot connect to API server. Please make sure the API server is running on http://localhost:8000',
          0,
          'Network error - API server may not be running'
        );
      }
      throw new ApiError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Health check endpoint
   */
  async checkHealth(): Promise<HealthResponse> {
    return this.fetchApi<HealthResponse>(API_CONFIG.endpoints.health);
  }

  /**
   * Get daily panchanga
   */
  async getDailyPanchanga(
    params: PanchangaRequestParams = {}
  ): Promise<PanchangaResponse> {
    return this.fetchApi<PanchangaResponse>(
      API_CONFIG.endpoints.dailyPanchanga,
      params
    );
  }

  /**
   * Get weekly panchanga
   */
  async getWeeklyPanchanga(
    params: WeeklyPanchangaRequestParams = {}
  ): Promise<WeeklyPanchangaResponse> {
    return this.fetchApi<WeeklyPanchangaResponse>(
      API_CONFIG.endpoints.weeklyPanchanga,
      params
    );
  }

  /**
   * Get monthly panchanga
   */
  async getMonthlyPanchanga(
    params: MonthlyPanchangaRequestParams = {}
  ): Promise<MonthlyPanchangaResponse> {
    return this.fetchApi<MonthlyPanchangaResponse>(
      API_CONFIG.endpoints.monthlyPanchanga,
      params
    );
  }

  /**
   * Get festivals for a date range
   */
  async getFestivals(
    params: FestivalRequestParams
  ): Promise<FestivalResponse[]> {
    return this.fetchApi<FestivalResponse[]>(
      API_CONFIG.endpoints.festivals,
      params
    );
  }

  /**
   * Get all available locations
   */
  async getLocations(): Promise<LocationInfo[]> {
    return this.fetchApi<LocationInfo[]>(API_CONFIG.endpoints.locations);
  }

  /**
   * Get supported locales
   */
  async getSupportedLocales(): Promise<SupportedLocales> {
    return this.fetchApi<SupportedLocales>(API_CONFIG.endpoints.locales);
  }

  /**
   * Get Tara Balam (favorable nakshatras) for a given nakshatra
   */
  async getTarabalam(params: TaraBalamRequestParams): Promise<TaraBalamResponse> {
    // Use POST request for tarabalam
    const base = this.baseUrl || window.location.origin;
    const url = new URL(`${base}${API_CONFIG.endpoints.tarabalam}`);
    
    try {
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.detail || `API error: ${response.status}`,
          response.status,
          errorData.detail
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(
          'Cannot connect to API server. Please make sure the API server is running on http://localhost:8000',
          0,
          'Network error - API server may not be running'
        );
      }
      throw new ApiError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
  /**
   * Get Muhurtha scores for a date range
   */
  async getMuhurtha(params: MuhurthaRequestParams): Promise<MuhurthaResponse> {
    return this.fetchApi<MuhurthaResponse>(
      API_CONFIG.endpoints.muhurtha,
      params as unknown as Record<string, string | number | undefined>
    );
  }

  /**
   * Get birth chart (kundali) from birth details
   */
  async getBirthChart(params: BirthChartRequestParams): Promise<BirthChartResponse> {
    return this.fetchApi<BirthChartResponse>(
      API_CONFIG.endpoints.birthChart,
      params as unknown as Record<string, string | number | undefined>
    );
  }

  /**
   * Get daily best times for a location
   */
  async getDailyBestTimes(params: DailyBestTimesRequestParams): Promise<DailyBestTimesResponse> {
    return this.fetchApi<DailyBestTimesResponse>(
      API_CONFIG.endpoints.dailyBestTimes,
      params as unknown as Record<string, string | number | undefined>
    );
  }

  /**
   * Search cities by name prefix (GeoNames database)
   */
  async searchCities(query: string, limit: number = 10): Promise<CitySearchResult[]> {
    return this.fetchApi<CitySearchResult[]>(
      API_CONFIG.endpoints.citySearch,
      { q: query, limit }
    );
  }
}

// Export singleton instance
export const panchangaApi = new PanchangaApiService();
export { ApiError };

